// pkg98 — MightyExchange™ (community module exchange, D-98b/d/e/f/g): browse + adopt (free, fork arrives OFF), the publish flow with the blocking integrity scan, and the mine view (listings w/ review outcomes + adoptions w/ the newer-version re-fork) — delivered 2026-07-18
//
// Naming rules applied: ™ once, on the page title (most prominent use on this
// surface); the nav link is bare "MightyExchange"; every branded action button
// carries a plain-English tail. pkg83a absolute — nothing here reaches any
// served or filed document (forks land in Document Modules and are the firm's
// own from that moment).

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const LISTING_STATUS_META = {
  submitted: { label: 'In review',  color: '#7c5200', bg: '#fff8e1', border: '#f5c842' },
  approved:  { label: 'Live',       color: '#1a3a2a', bg: '#f0faf5', border: '#4caf7d' },
  rejected:  { label: 'Rejected',   color: '#8a1f1f', bg: '#fdf0f0', border: '#e08080' },
  delisted:  { label: 'Withdrawn',  color: '#555',    bg: '#f5f5f5', border: '#ccc' },
};

function StatusBadge({ status }) {
  const m = LISTING_STATUS_META[status] || LISTING_STATUS_META.delisted;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: m.color, background: m.bg,
      border: `1px solid ${m.border}`, borderRadius: 4, padding: '1px 8px' }}>
      {m.label}
    </span>
  );
}

function StateBadge({ state, notStateSpecific }) {
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: '#1a2a4a', background: '#eef3fb',
      border: '1px solid #b9cbe8', borderRadius: 4, padding: '1px 8px' }}>
      {state || (notStateSpecific ? 'All states' : '—')}
    </span>
  );
}

function adoptedModuleId(result) {
  if (!result) return null;
  return result.firm_module_id || result.module_id || result.fork_id
    || (result.module && result.module.id)
    || (result.firm_module && result.firm_module.id)
    || null;
}

function groupHits(hits) {
  const groups = new Map();
  (hits || []).forEach((hit) => {
    const part = hit.part || 'Document';
    if (!groups.has(part)) groups.set(part, []);
    groups.get(part).push(hit);
  });
  return Array.from(groups.entries());
}

function publishFailure(e) {
  if (e.status === 403) return 'Only an owner or an attorney can share a document.';
  if (e.code === 'EXCHANGE_PENDING_CAP') {
    const count = e.pending_count;
    const limit = e.pending_limit;
    if (count != null && limit != null) {
      return `You have ${count} of ${limit} documents waiting for review. Withdraw one, or wait for a decision, before sharing another.`;
    }
    return e.message || 'You already have the maximum number of documents waiting for review.';
  }
  if (e.code === 'EXCHANGE_SCAN_BLOCKED') {
    return e.message || 'The scan still found text that has to be converted before this document can be shared.';
  }
  return e.message;
}

function fieldCount(schema) {
  // Rough field tally for the preview line — counts leaf field objects in the
  // common schema shapes (blocks/fields arrays) without assuming one format.
  let n = 0;
  const walk = (v) => {
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (v && typeof v === 'object') {
      if (v.label && (v.type || v.field || v.id)) n++;
      Object.values(v).forEach(walk);
    }
  };
  walk(schema || {});
  return n;
}

export default function AdminExchangePage() {
  const [tab, setTab] = useState('browse'); // 'browse' | 'mine'
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Browse filters
  const [fState, setFState] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fQ, setFQ] = useState('');
  const [fSort, setFSort] = useState('adoptions');

  // Detail
  const [detail, setDetail] = useState(null); // { listing, current }
  const [detailBusy, setDetailBusy] = useState(false);
  const [adoptBusy, setAdoptBusy] = useState(false);

  // Mine
  const [mine, setMine] = useState(null); // { published, adoptions }

  // Publish flow
  const [showPublish, setShowPublish] = useState(false);
  const [myModules, setMyModules] = useState(null);
  const [pubModuleId, setPubModuleId] = useState('');
  const [pubCategory, setPubCategory] = useState('practice_area');
  const [pubState, setPubState] = useState('');
  const [pubNotStateSpecific, setPubNotStateSpecific] = useState(false);
  const [pubCaseType, setPubCaseType] = useState('');
  const [pubAttest, setPubAttest] = useState(false);
  const [pubBusy, setPubBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const [convertingId, setConvertingId] = useState('');
  const [adopted, setAdopted] = useState(null);

  const loadBrowse = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { sort: fSort };
      if (fState) params.state = fState;
      if (fCategory) params.category = fCategory;
      if (fQ.trim()) params.q = fQ.trim();
      const r = await adminApi.exchangeBrowse(params);
      setListings(r.listings || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fState, fCategory, fQ, fSort]);

  const loadMine = useCallback(async () => {
    try {
      const r = await adminApi.exchangeMine();
      setMine(r);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { loadBrowse(); }, [loadBrowse]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const openDetail = async (id) => {
    setDetailBusy(true);
    setError('');
    try {
      const r = await adminApi.exchangeGet(id);
      setDetail(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const adopt = async (id) => {
    setAdoptBusy(true);
    setError('');
    setNotice('');
    try {
      const r = await adminApi.exchangeAdopt(id);
      const moduleId = adoptedModuleId(r);
      const hasDocument = !!(r.has_document || (r.current && r.current.has_document) || (detail && detail.current && detail.current.has_document));
      setAdopted({ moduleId, hasDocument, message: r.message || 'Adopted.' });
      setNotice(r.message || 'Adopted. The copy is in your documents, switched off until you review it.');
      setDetail(null);
      loadMine();
      loadBrowse();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdoptBusy(false);
    }
  };

  const withdraw = async (id) => {
    if (!window.confirm('Withdraw this listing from MightyExchange? Firms that already adopted it keep their copies.')) return;
    setError('');
    try {
      const r = await adminApi.exchangeWithdraw(id);
      setNotice(r.message || 'Withdrawn.');
      loadMine();
      loadBrowse();
    } catch (e) {
      setError(e.message);
    }
  };

  const openPublish = async (preset) => {
    setShowPublish(true);
    setScan(null);
    setNotice('');
    setError('');
    if (preset) {
      if (preset.firm_module_id) setPubModuleId(preset.firm_module_id);
      setPubState(preset.state || '');
      setPubNotStateSpecific(!!preset.not_state_specific && !preset.state);
      setPubCaseType(preset.case_type_label || '');
      setPubCategory(preset.category || 'practice_area');
      setPubAttest(false);
    }
    if (myModules === null) {
      try {
        const r = await adminApi.listFirmModules();
        const list = Array.isArray(r) ? r : (r.modules || []);
        setMyModules(list.filter((m) => m.status === 'active'));
      } catch (e) {
        setError(e.message);
        setMyModules([]);
      }
    }
  };

  const runScan = async () => {
    setError('');
    setScan(null);
    if (!pubModuleId) { setError('Pick a document to share.'); return; }
    setPubBusy(true);
    try {
      const r = await adminApi.exchangePublishScan(pubModuleId);
      setScan(r);
    } catch (e) {
      setError(publishFailure(e));
      if (e.code === 'EXCHANGE_SCAN_BLOCKED' && Array.isArray(e.hits)) {
        setScan({ ok: false, hits: e.hits, pending_count: e.pending_count, pending_limit: e.pending_limit });
      }
    } finally {
      setPubBusy(false);
    }
  };

  const convertHit = async (hit) => {
    if (!scan || !scan.scan_id) return;
    setError('');
    setConvertingId(hit.id);
    try {
      const r = await adminApi.exchangePublishConvert(scan.scan_id, hit.id);
      setScan(r);
    } catch (e) {
      setError(publishFailure(e));
    } finally {
      setConvertingId('');
    }
  };

  const submitPublish = async () => {
    setError('');
    if (!pubModuleId) { setError('Pick a document to share.'); return; }
    if (!scan || !scan.ok || !scan.scan_id) { setError('Run the scan and clear every hit before submitting.'); return; }
    if (!pubState && !pubNotStateSpecific) { setError('Assign a state, or mark the document as not specific to one state.'); return; }
    if (pubCategory === 'practice_area' && !pubCaseType.trim()) { setError('Enter the case type.'); return; }
    if (!pubAttest) { setError('The attestation is required.'); return; }
    setPubBusy(true);
    try {
      const r = await adminApi.exchangePublish({
        scan_id: scan.scan_id,
        firm_module_id: pubModuleId,
        category: pubCategory,
        state: pubState || null,
        not_state_specific: !pubState,
        case_type_label: pubCategory === 'practice_area' ? pubCaseType.trim() : null,
        attest_no_confidential: true,
      });
      setNotice(r.message || 'Submitted for review.');
      setShowPublish(false);
      setScan(null);
      setPubModuleId(''); setPubState(''); setPubNotStateSpecific(false);
      setPubCaseType(''); setPubAttest(false);
      loadMine();
    } catch (e) {
      setError(publishFailure(e));
      if (e.code === 'EXCHANGE_SCAN_BLOCKED') {
        setScan((prev) => ({ ...(prev || {}), ok: false, hits: e.hits || (prev && prev.hits) || [] }));
      }
    } finally {
      setPubBusy(false);
    }
  };

  const card = { background: '#fff', border: '1px solid #e2e2e2', borderRadius: 8, padding: 16, marginBottom: 12 };
  const tabBtn = (key, label) => (
    <button className="btn btn-sm" onClick={() => setTab(key)}
      style={tab === key ? { fontWeight: 700, textDecoration: 'underline' } : {}}>
      {label}
    </button>
  );

  return (
    <div>
      <h1 className="page-title">MightyExchange™ — community documents</h1>
      <p className="page-subtitle">
        Dynamic documents shared by law firms. Adopting is free and copies a snapshot into your
        own documents, switched off until you review it and fill it. Verify each one fits your
        jurisdiction and practice.
      </p>

      {notice && <div style={{ ...card, background: '#f0faf5', borderColor: '#4caf7d' }}>{notice}</div>}
      {error && <div style={{ ...card, background: '#fdf0f0', borderColor: '#e08080' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
        {tabBtn('browse', 'Browse the exchange')}
        {tabBtn('mine', 'My listings & adoptions')}
        <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => openPublish()}>
          ✦ Share a document
        </button>
      </div>

      {/* ── Publish flow ─────────────────────────────────────────────────── */}
      {showPublish && (
        <div style={{ ...card, borderColor: '#b9cbe8' }}>
          <h3 style={{ marginTop: 0 }}>Share a dynamic document</h3>
          <p style={{ fontSize: 13, color: '#555' }}>
            Only an active document can be shared. The scan checks the Word file, then you convert
            anything that still looks like a name, address, or other private detail. LawMighty
            reviews it before it appears. Your firm's name is on the listing.
            {mine && mine.pending_limit != null && (
              <> You have {mine.pending_count || 0} of {mine.pending_limit} documents waiting for review.</>
            )}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <label style={{ fontSize: 13 }}>
              Document<br />
              <select value={pubModuleId} onChange={(e) => { setPubModuleId(e.target.value); setScan(null); }}>
                <option value="">— pick an active document —</option>
                {(myModules || []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 13 }}>
              Category<br />
              <select value={pubCategory} onChange={(e) => setPubCategory(e.target.value)}>
                <option value="practice_area">Practice area</option>
                <option value="operations">Firm operations</option>
              </select>
            </label>
            {pubCategory === 'practice_area' && (
              <label style={{ fontSize: 13 }}>
                Case type<br />
                <input value={pubCaseType} onChange={(e) => setPubCaseType(e.target.value)}
                  placeholder="e.g. Divorce" />
              </label>
            )}
            <label style={{ fontSize: 13 }}>
              State<br />
              <select value={pubState}
                onChange={(e) => { setPubState(e.target.value); if (e.target.value) setPubNotStateSpecific(false); }}>
                <option value="">— no state —</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>

          {!pubState && (
            <label style={{ display: 'block', fontSize: 13, marginTop: 10 }}>
              <input type="checkbox" checked={pubNotStateSpecific}
                onChange={(e) => setPubNotStateSpecific(e.target.checked)} />{' '}
              I attest this document is <strong>not specific to any state</strong> and is usable as-is
              in any U.S. jurisdiction.
            </label>
          )}
          <label style={{ display: 'block', fontSize: 13, marginTop: 6 }}>
            <input type="checkbox" checked={pubAttest}
              onChange={(e) => setPubAttest(e.target.checked)} />{' '}
            I attest this document contains <strong>no confidential, client, or firm-identifying
            data</strong> — no names, addresses, phone numbers, or email addresses.
          </label>

          {scan && scan.metadata_stripped && (
            <p style={{ fontSize: 13, color: '#1a3a2a', background: '#f0faf5', border: '1px solid #4caf7d', borderRadius: 6, padding: 10, marginTop: 10 }}>
              Author, comments, and other document properties were stripped from the copy that will be shared.
            </p>
          )}
          {scan && scan.ai && scan.ai.skipped && (
            <p style={{ fontSize: 13, color: '#7c5200', background: '#fff8e1', border: '1px solid #f5c842', borderRadius: 6, padding: 10, marginTop: 10 }}>
              The AI pass was skipped{scan.ai.reason ? `: ${scan.ai.reason}` : '.'}
              {scan.ai.error ? ` ${scan.ai.error}` : ''}
            </p>
          )}
          {scan && scan.ok && (
            <p style={{ fontSize: 13, color: '#1a3a2a', marginTop: 10 }}>
              The scan is clear. You can submit this document for review.
            </p>
          )}
          {scan && !scan.ok && Array.isArray(scan.hits) && scan.hits.length > 0 && (
            <div style={{ marginTop: 10, background: '#fdf0f0', border: '1px solid #e08080', borderRadius: 6, padding: 12 }}>
              <strong>The scan found text that has to be converted.</strong>
              <p style={{ fontSize: 13, margin: '6px 0' }}>
                Each item stays in the shared copy as a blank instead of the original text. Nothing is published until the scan is clear.
              </p>
              {groupHits(scan.hits).map(([part, hits]) => (
                <div key={part} style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#555' }}>{part}</div>
                  <ul style={{ fontSize: 13, margin: '6px 0 0', paddingLeft: 18 }}>
                    {hits.map((h) => (
                      <li key={h.id} style={{ marginBottom: 8 }}>
                        <div><strong>{h.kind}:</strong> “{h.excerpt}”</div>
                        {h.action_label && (
                          <button type="button" className="btn btn-sm" style={{ marginTop: 4 }}
                            disabled={!!convertingId || !scan.scan_id}
                            onClick={() => convertHit(h)}>
                            {convertingId === h.id ? 'Converting…' : h.action_label}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={runScan} disabled={pubBusy || !!convertingId}>
              {pubBusy && !scan ? 'Scanning…' : 'Run scan'}
            </button>
            <button className="btn btn-sm" onClick={submitPublish} disabled={pubBusy || !scan || !scan.ok || !pubAttest}>
              {pubBusy && scan ? 'Submitting…' : 'Submit for review'}
            </button>
            <button className="btn btn-sm" onClick={() => { setShowPublish(false); setScan(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Browse ───────────────────────────────────────────────────────── */}
      {tab === 'browse' && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 14 }}>
            <label style={{ fontSize: 13 }}>
              State<br />
              <select value={fState} onChange={(e) => setFState(e.target.value)}>
                <option value="">All states</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 13 }}>
              Category<br />
              <select value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                <option value="">All</option>
                <option value="practice_area">Practice area</option>
                <option value="operations">Firm operations</option>
              </select>
            </label>
            <label style={{ fontSize: 13 }}>
              Search<br />
              <input value={fQ} onChange={(e) => setFQ(e.target.value)} placeholder="intake, retainer, checklist…"
                onKeyDown={(e) => { if (e.key === 'Enter') loadBrowse(); }} />
            </label>
            <label style={{ fontSize: 13 }}>
              Sort<br />
              <select value={fSort} onChange={(e) => setFSort(e.target.value)}>
                <option value="adoptions">Most adopted</option>
                <option value="newest">Newest</option>
              </select>
            </label>
          </div>

          {loading ? <p>Loading…</p> : !listings.length ? (
            <div style={card}>
              <p style={{ margin: 0 }}>
                Nothing here yet{fState || fCategory || fQ ? ' for these filters' : ''}. Be the first —
                share one of your documents and your firm's name goes on the listing.
              </p>
            </div>
          ) : listings.map((l) => (
            <div key={l.id} style={card}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {l.featured && <span title="Featured by LawMighty">★</span>}
                <strong>{l.label}</strong>
                <StateBadge state={l.state} notStateSpecific={l.not_state_specific} />
                <span style={{ fontSize: 12, color: '#777' }}>
                  {l.category === 'operations' ? 'Firm operations' : (l.practice_area_label || 'Practice area')}
                  {l.case_type_label ? ` · ${l.case_type_label}` : ''}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#777' }}>
                  Adopted by {l.adoptions_total} firm{l.adoptions_total === 1 ? '' : 's'}
                </span>
              </div>
              {l.description && <p style={{ fontSize: 13, color: '#444', margin: '8px 0' }}>{l.description}</p>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#555' }}>
                  By {l.firm_name}{l.firm_city ? ` — ${l.firm_city}` : ''}{l.firm_state ? `, ${l.firm_state}` : ''}
                </span>
                <button className="btn btn-sm" style={{ marginLeft: 'auto' }}
                  onClick={() => openDetail(l.id)} disabled={detailBusy}>
                  View details
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* ── Detail panel ─────────────────────────────────────────────────── */}
      {detail && (
        <div style={{ ...card, borderColor: '#b9cbe8' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>{detail.listing.label}</h3>
            <StateBadge state={detail.listing.state} notStateSpecific={detail.listing.not_state_specific} />
            <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setDetail(null)}>Close</button>
          </div>
          <p style={{ fontSize: 13, color: '#555', margin: '8px 0' }}>
            By {detail.listing.firm_name}
            {detail.listing.firm_city ? ` — ${detail.listing.firm_city}` : ''}
            {detail.listing.firm_state ? `, ${detail.listing.firm_state}` : ''}
            {' · '}v{detail.listing.current_version}
            {' · '}Adopted by {detail.listing.adoptions_total} firm{detail.listing.adoptions_total === 1 ? '' : 's'}
          </p>
          {detail.listing.description && <p style={{ fontSize: 13 }}>{detail.listing.description}</p>}
          {detail.current && (
            <p style={{ fontSize: 13, color: '#555' }}>
              Dynamic document
              {' · about '}{fieldCount(detail.current.schema_json)} fields
              {detail.current.audience ? ` · audience: ${detail.current.audience}` : ''}
              {' · '}{detail.current.has_document ? 'Includes the shared Word document.' : 'No Word document is attached to this version.'}
            </p>
          )}
          <p style={{ fontSize: 12, color: '#777' }}>
            Adopting copies a snapshot into your documents, switched off until you review it.
            It never updates itself — if the contributor publishes a newer version, you can adopt
            that version as a separate copy.
          </p>
          {!detail.is_own && (
            <button className="btn btn-sm" onClick={() => adopt(detail.listing.id)} disabled={adoptBusy}>
              {adoptBusy ? 'Adopting…' : '✦ Adopt — copy into my documents (free)'}
            </button>
          )}
        </div>
      )}

      {adopted && (
        <div style={{ ...card, background: '#f0faf5', borderColor: '#4caf7d' }}>
          <strong>{adopted.message}</strong>
          <p style={{ fontSize: 13, margin: '6px 0' }}>
            {adopted.hasDocument
              ? 'The copy includes the shared document and is ready to fill.'
              : 'This version did not include a Word document. You can still review the fields.'}
          </p>
          {adopted.moduleId ? (
            <Link className="btn btn-sm" to={`/admin/dashboard/firm-modules/${adopted.moduleId}/fill`}>
              Open the document and fill it
            </Link>
          ) : (
            <Link className="btn btn-sm" to="/admin/dashboard/firm-modules">Open your documents</Link>
          )}
        </div>
      )}

      {/* ── Mine ─────────────────────────────────────────────────────────── */}
      {tab === 'mine' && (
        <>
          <h3>My listings</h3>
          {mine && mine.pending_limit != null && (
            <p style={{ fontSize: 13, color: '#555' }}>
              {mine.pending_count || 0} of {mine.pending_limit} documents waiting for review.
            </p>
          )}
          {!mine ? <p>Loading…</p> : !(mine.published || []).length ? (
            <p style={{ fontSize: 13, color: '#555' }}>You haven't shared any documents yet.</p>
          ) : (mine.published || []).map((l) => (
            <div key={l.id} style={card}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>{l.label}</strong>
                <StatusBadge status={l.status} />
                <StateBadge state={l.state} notStateSpecific={l.not_state_specific} />
                <span style={{ fontSize: 12, color: '#777' }}>v{l.current_version}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#777' }}>
                  Adopted by {l.adoptions_total} firm{l.adoptions_total === 1 ? '' : 's'}
                </span>
                {(l.status === 'approved' || l.status === 'submitted') && (
                  <button className="btn btn-sm" onClick={() => withdraw(l.id)}>Withdraw</button>
                )}
              </div>
              {l.status === 'rejected' && (
                <div style={{ marginTop: 8 }}>
                  <p style={{ fontSize: 13, color: '#8a1f1f', margin: '0 0 8px' }}>
                    {l.review_note ? `Rejected: ${l.review_note}` : 'Rejected. No note was saved.'}
                  </p>
                  <button className="btn btn-sm" onClick={() => openPublish(l)}>Revise and resubmit</button>
                </div>
              )}
            </div>
          ))}

          <h3>My adoptions</h3>
          {!mine ? null : !(mine.adoptions || []).length ? (
            <p style={{ fontSize: 13, color: '#555' }}>You haven't adopted any community documents yet.</p>
          ) : mine.adoptions.map((a) => (
            <div key={a.id} style={card}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>{a.label}</strong>
                <StateBadge state={a.state} notStateSpecific={a.not_state_specific} />
                <span style={{ fontSize: 12, color: '#777' }}>
                  by {a.firm_name} · adopted v{a.adopted_version} on {new Date(a.adopted_at).toLocaleDateString()}
                </span>
                {a.newer_version_available && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#7c5200' }}>
                      Newer version available (v{a.current_version})
                    </span>
                    <button className="btn btn-sm" onClick={() => adopt(a.exchange_module_id)} disabled={adoptBusy}>
                      Adopt latest as a separate copy
                    </button>
                  </span>
                )}
              </div>
              <p style={{ fontSize: 12, color: '#777', margin: '6px 0 0' }}>
                Your copy lives with your documents and never changes unless you change it.
              </p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
